import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import Loader from "../../components/Loader/Loader";
import { useAuth } from "../../context/AuthContext";
import {
  getConsultationService,
  getConsultantSlots,
  createConsultationBooking,
  submitPayuForm,
} from "../../api/consultationApi";
import { ConsultantCard, ConsultantPhoto } from "./ConsultantCard";
import {
  dateChipParts,
  formatINR,
  formatSlotDateTime,
  formatSlotTime,
} from "./consultationUtils";
import "./Consultation.css";

const STEPS = ["Consultant", "Date & time", "Your details", "Review & pay"];

const DEFAULT_CONCERNS = ["Career", "Business", "Marriage", "Finance", "Wellbeing", "General guidance"];

const emptyDetails = {
  name: "",
  email: "",
  phone: "",
  dateOfBirth: "",
  timeOfBirth: "",
  placeOfBirth: "",
  concerns: [],
  concernNote: "",
};

export default function ConsultationBooking() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const { user, accessToken } = useAuth();

  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [step, setStep] = useState(1);
  const [consultant, setConsultant] = useState(null);

  const [slotData, setSlotData] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedSlot, setSelectedSlot] = useState("");

  const [details, setDetails] = useState(emptyDetails);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const needsBirthDetails = service?.category === "astrology";
  const concernOptions = service?.content?.concernOptions?.length
    ? service.content.concernOptions
    : DEFAULT_CONCERNS;

  /* ------------------------------------------------------------- service */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await getConsultationService(slug);
        if (cancelled) return;
        if (!result.ok || !result.success) throw new Error(result.status === 404 ? "Service not found" : result.message || "Unable to load service");
        setService(result.data);

        const wanted = searchParams.get("consultant");
        const preselected = result.data.consultants.find((c) => c.id === wanted);
        if (preselected) {
          setConsultant(preselected);
          setStep(2);
        }
      } catch (e) {
        if (!cancelled) setLoadError(e.message || "Unable to load service");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Prefill from the logged-in account, without overwriting what was typed.
  useEffect(() => {
    if (!user) return;
    setDetails((d) => ({
      ...d,
      name: d.name || user.name || "",
      email: d.email || user.email || "",
      phone: d.phone || user.phone || "",
    }));
  }, [user]);

  /* --------------------------------------------------------------- slots */
  const loadSlots = async (target) => {
    if (!target) return;
    try {
      setSlotsLoading(true);
      setSlotsError("");
      const result = await getConsultantSlots(target.id, 21);
      if (!result.ok || !result.success) throw new Error(result.message || "Unable to load slots");
      setSlotData(result.data);
      const first = result.data.days[0]?.date || "";
      setSelectedDate((current) => (result.data.days.some((d) => d.date === current) ? current : first));
    } catch (e) {
      setSlotsError(e.message || "Unable to load slots");
    } finally {
      setSlotsLoading(false);
    }
  };

  useEffect(() => {
    if (consultant) {
      setSelectedSlot("");
      loadSlots(consultant);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consultant?.id]);

  const daySlots = useMemo(
    () => slotData?.days.find((d) => d.date === selectedDate)?.slots || [],
    [slotData, selectedDate]
  );

  /* ------------------------------------------------------------- details */
  const setField = (name, value) => {
    setDetails((d) => ({ ...d, [name]: value }));
    setErrors((e) => ({ ...e, [name]: "" }));
  };

  const toggleConcern = (value) =>
    setDetails((d) => ({
      ...d,
      concerns: d.concerns.includes(value) ? d.concerns.filter((c) => c !== value) : [...d.concerns, value],
    }));

  const validateDetails = () => {
    const next = {};
    if (!details.name.trim()) next.name = "Please enter your name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(details.email.trim())) next.email = "Enter a valid email";
    if (!/^\+?[0-9\s-]{10,16}$/.test(details.phone.trim())) next.phone = "Enter a valid phone number";
    if (needsBirthDetails) {
      if (!details.dateOfBirth) next.dateOfBirth = "Date of birth is required";
      if (!details.timeOfBirth) next.timeOfBirth = "Time of birth is required";
      if (!details.placeOfBirth.trim()) next.placeOfBirth = "Place of birth is required";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  /* -------------------------------------------------------------- submit */
  const pay = async () => {
    setSubmitError("");
    setSubmitting(true);
    try {
      const result = await createConsultationBooking(
        {
          serviceId: service.id,
          consultantId: consultant.id,
          slotStart: selectedSlot,
          name: details.name,
          email: details.email,
          phone: details.phone,
          dateOfBirth: details.dateOfBirth,
          timeOfBirth: details.timeOfBirth,
          placeOfBirth: details.placeOfBirth,
          concerns: details.concerns,
          concernNote: details.concernNote,
        },
        accessToken
      );

      if (!result.ok || !result.success) {
        if (result.status === 409) {
          // Someone took the slot first - refresh and send the user back.
          setSelectedSlot("");
          await loadSlots(consultant);
          setStep(2);
        }
        throw new Error(result.message || "Unable to create your booking");
      }

      if (!result.data?.payuPayment?.action) throw new Error("Payment could not be started. Please try again.");
      submitPayuForm(result.data.payuPayment);
    } catch (e) {
      setSubmitError(e.message || "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  /* -------------------------------------------------------------- render */
  if (loading) {
    return (
      <>
        <Header />
        <div className="cn-state"><Loader label="Loading..." /></div>
        <Footer />
      </>
    );
  }

  if (loadError || !service) {
    return (
      <>
        <Header />
        <div className="cn-state">
          <h2>{loadError || "Service not found"}</h2>
          <Link className="cn-btn cn-btn-gold" to="/consultation">Browse consultations</Link>
        </div>
        <Footer />
      </>
    );
  }

  const canContinueFromSlot = Boolean(selectedSlot);

  return (
    <>
      <Header />

      <section className="cn-hero cn-hero-compact">
        <div className="cn-hero-inner">
          <nav className="cn-crumbs" aria-label="Breadcrumb">
            <Link to="/consultation">Consultation</Link> /{" "}
            <Link to={`/consultation/${service.category}/${service.slug}`}>{service.title}</Link>
          </nav>
          <h1>Book: {service.title}</h1>
        </div>
      </section>

      <section className="cn-section">
        <div className="cn-container cn-book">
          <ol className="cn-stepper" aria-label="Booking progress">
            {STEPS.map((label, i) => {
              const n = i + 1;
              return (
                <li key={label} className={n === step ? "is-current" : n < step ? "is-done" : ""} aria-current={n === step ? "step" : undefined}>
                  <span>{n < step ? "✓" : n}</span>
                  <em>{label}</em>
                </li>
              );
            })}
          </ol>

          <div className="cn-book-grid">
            <div className="cn-panel">
              {/* STEP 1 - CONSULTANT */}
              {step === 1 && (
                <>
                  <h2>Choose your consultant</h2>
                  {service.consultants.length === 0 ? (
                    <p className="cn-state">No consultants are available for this service yet.</p>
                  ) : (
                    <div className="cn-consultants cn-consultants-1">
                      {service.consultants.map((c) => (
                        <ConsultantCard
                          key={c.id}
                          consultant={c}
                          selected={consultant?.id === c.id}
                          onSelect={(picked) => {
                            setConsultant(picked);
                            setStep(2);
                          }}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* STEP 2 - SLOT */}
              {step === 2 && consultant && (
                <>
                  <h2>Select date &amp; time</h2>
                  <p className="cn-muted">All times are in Indian Standard Time (IST).</p>

                  {slotsLoading ? (
                    <Loader label="Loading available slots..." />
                  ) : slotsError ? (
                    <p className="cn-error">{slotsError}</p>
                  ) : !slotData || slotData.days.length === 0 ? (
                    <p className="cn-state">No slots are open in the next three weeks. Please check back soon or choose another consultant.</p>
                  ) : (
                    <>
                      <div className="cn-dates" role="listbox" aria-label="Choose a date">
                        {slotData.days.map((d) => {
                          const p = dateChipParts(d.date);
                          return (
                            <button
                              key={d.date}
                              type="button"
                              role="option"
                              aria-selected={selectedDate === d.date}
                              className={`cn-date ${selectedDate === d.date ? "is-active" : ""}`}
                              onClick={() => {
                                setSelectedDate(d.date);
                                setSelectedSlot("");
                              }}
                            >
                              <small>{p.day}</small>
                              <strong>{p.date}</strong>
                              <small>{p.month}</small>
                            </button>
                          );
                        })}
                      </div>

                      <div className="cn-times" role="listbox" aria-label="Choose a time">
                        {daySlots.map((s) => (
                          <button
                            key={s.start}
                            type="button"
                            role="option"
                            aria-selected={selectedSlot === s.start}
                            disabled={!s.available}
                            className={`cn-time ${selectedSlot === s.start ? "is-active" : ""}`}
                            onClick={() => setSelectedSlot(s.start)}
                          >
                            {formatSlotTime(s.start)}
                          </button>
                        ))}
                      </div>
                    </>
                  )}

                  <div className="cn-actions">
                    <button type="button" className="cn-btn cn-btn-ghost" onClick={() => setStep(1)}>
                      Change consultant
                    </button>
                    <button type="button" className="cn-btn cn-btn-gold" disabled={!canContinueFromSlot} onClick={() => setStep(3)}>
                      Continue
                    </button>
                  </div>
                </>
              )}

              {/* STEP 3 - DETAILS */}
              {step === 3 && (
                <>
                  <h2>Your details</h2>
                  <form
                    className="cn-form"
                    noValidate
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (validateDetails()) setStep(4);
                    }}
                  >
                    <Field label="Full name" error={errors.name}>
                      <input value={details.name} autoComplete="name" onChange={(e) => setField("name", e.target.value)} />
                    </Field>
                    <div className="cn-row">
                      <Field label="Email" error={errors.email}>
                        <input type="email" inputMode="email" autoComplete="email" value={details.email} onChange={(e) => setField("email", e.target.value)} />
                      </Field>
                      <Field label="Phone / WhatsApp" error={errors.phone}>
                        <input type="tel" inputMode="tel" autoComplete="tel" value={details.phone} onChange={(e) => setField("phone", e.target.value)} />
                      </Field>
                    </div>

                    <h3 className="cn-sub">Birth details {needsBirthDetails ? "" : "(optional)"}</h3>
                    <p className="cn-muted">Accuracy of birth details is important for analysis.</p>
                    <div className="cn-row cn-row-3">
                      <Field label="Date of birth" error={errors.dateOfBirth}>
                        <input type="date" value={details.dateOfBirth} onChange={(e) => setField("dateOfBirth", e.target.value)} />
                      </Field>
                      <Field label="Exact time of birth" error={errors.timeOfBirth}>
                        <input type="time" value={details.timeOfBirth} onChange={(e) => setField("timeOfBirth", e.target.value)} />
                      </Field>
                      <Field label="Place of birth" error={errors.placeOfBirth}>
                        <input value={details.placeOfBirth} onChange={(e) => setField("placeOfBirth", e.target.value)} />
                      </Field>
                    </div>

                    <h3 className="cn-sub">What would you like to understand?</h3>
                    <div className="cn-chips">
                      {concernOptions.map((o) => (
                        <button
                          type="button"
                          key={o}
                          aria-pressed={details.concerns.includes(o)}
                          className={`cn-chip cn-chip-btn ${details.concerns.includes(o) ? "is-active" : ""}`}
                          onClick={() => toggleConcern(o)}
                        >
                          {o}
                        </button>
                      ))}
                    </div>
                    <Field label="Tell us more (optional)">
                      <textarea rows="4" maxLength={2000} value={details.concernNote} onChange={(e) => setField("concernNote", e.target.value)} />
                    </Field>

                    <div className="cn-actions">
                      <button type="button" className="cn-btn cn-btn-ghost" onClick={() => setStep(2)}>
                        Back
                      </button>
                      <button type="submit" className="cn-btn cn-btn-gold">
                        Review booking
                      </button>
                    </div>
                  </form>
                </>
              )}

              {/* STEP 4 - REVIEW & PAY */}
              {step === 4 && (
                <>
                  <h2>Review &amp; pay</h2>
                  <dl className="cn-review">
                    <div><dt>Consultation</dt><dd>{service.title}</dd></div>
                    <div><dt>Consultant</dt><dd>{consultant.name}</dd></div>
                    <div><dt>Date &amp; time</dt><dd>{formatSlotDateTime(selectedSlot)} IST</dd></div>
                    <div><dt>Duration</dt><dd>{consultant.durationMinutes} minutes</dd></div>
                    <div><dt>Name</dt><dd>{details.name}</dd></div>
                    <div><dt>Contact</dt><dd>{details.email} · {details.phone}</dd></div>
                    {details.dateOfBirth && (
                      <div><dt>Birth details</dt><dd>{details.dateOfBirth} {details.timeOfBirth} · {details.placeOfBirth}</dd></div>
                    )}
                    {details.concerns.length > 0 && <div><dt>Concerns</dt><dd>{details.concerns.join(", ")}</dd></div>}
                  </dl>

                  <p className="cn-hold">Your slot is held for 15 minutes once you proceed to payment.</p>
                  {submitError && <p className="cn-error" role="alert">{submitError}</p>}

                  <div className="cn-actions">
                    <button type="button" className="cn-btn cn-btn-ghost" disabled={submitting} onClick={() => setStep(3)}>
                      Back
                    </button>
                    <button type="button" className="cn-btn cn-btn-gold" disabled={submitting} onClick={pay}>
                      {submitting ? "Please wait..." : `Pay ${formatINR(consultant.fee)} securely`}
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* SUMMARY */}
            <aside className="cn-summary" aria-label="Booking summary">
              <h3>Your booking</h3>
              <p className="cn-summary-service">{service.title}</p>
              {consultant ? (
                <div className="cn-summary-consultant">
                  <ConsultantPhoto consultant={consultant} size="sm" />
                  <div>
                    <strong>{consultant.name}</strong>
                    <small>{consultant.durationMinutes} min</small>
                  </div>
                </div>
              ) : (
                <p className="cn-muted">No consultant selected yet.</p>
              )}
              {selectedSlot && <p className="cn-summary-slot">{formatSlotDateTime(selectedSlot)}</p>}
              {consultant && (
                <p className="cn-summary-total">
                  <span>Total</span> <strong>{formatINR(consultant.fee)}</strong>
                </p>
              )}
              <small className="cn-muted">Secure payment via PayU · UPI, cards &amp; net banking</small>
            </aside>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}

function Field({ label, error, children }) {
  return (
    <label className={`cn-field ${error ? "has-error" : ""}`}>
      <span>{label}</span>
      {children}
      {error && <em role="alert">{error}</em>}
    </label>
  );
}
